import { useState, useEffect, useCallback, useMemo } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { toast } from 'lib/toast';
import { apiNotifications } from 'services/apiService';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  type NotificationPreferences,
  type NotificationCategory,
  type NotificationPriority,
} from 'types/notificationsTypes';

interface UseNotificationPreferencesReturn {
  preferences: NotificationPreferences;
  isLoading: boolean;
  error: string | null;
  updatePreferences: (updates: Partial<NotificationPreferences>) => Promise<void>;
  toggleCategory: (category: NotificationCategory, enabled: boolean) => Promise<void>;
  setCategoryThreshold: (
    category: NotificationCategory,
    threshold: NotificationPriority
  ) => Promise<void>;
  toggleDND: (enabled: boolean) => Promise<void>;
  setDNDSchedule: (startTime: string, endTime: string, weekends: boolean) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  isDNDActive: boolean;
}

interface MutationContext {
  previousPreferences?: NotificationPreferences;
}

/**
 * Parses a time string in HH:mm format to minutes since midnight
 */
const parseTimeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Checks if current time is within a DND time window
 * Handles overnight windows (e.g., 22:00 to 08:00)
 */
const isTimeInDNDWindow = (
  currentMinutes: number,
  startMinutes: number,
  endMinutes: number
): boolean => {
  // Overnight window (e.g., 22:00 to 08:00)
  if (startMinutes > endMinutes) {
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }
  // Same day window (e.g., 09:00 to 17:00)
  return currentMinutes >= startMinutes && currentMinutes < endMinutes;
};

/**
 * Hook for managing notification preferences with DND support
 *
 * @param userId - The user ID to fetch/update preferences for
 * @returns Notification preferences state and actions
 */
export const useNotificationPreferences = (
  userId: string | undefined
): UseNotificationPreferencesReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('toast');
  const [isDNDActive, setIsDNDActive] = useState(false);

  // Query for fetching preferences
  const {
    data: preferences,
    isLoading,
    error: queryError,
  } = useQuery<NotificationPreferences, Error>({
    queryKey: ['notification-preferences', userId],
    queryFn: async () => {
      const response = await apiNotifications.get<NotificationPreferences>(
        `/v1/preferences/${userId}`
      );
      return response.data;
    },
    // TODO: Re-enable when backend endpoint /notifications/v1/preferences is available
    enabled: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Use preferences from query or fallback to defaults
  const currentPreferences = useMemo(
    () => preferences ?? DEFAULT_NOTIFICATION_PREFERENCES,
    [preferences]
  );

  // Mutation for updating preferences
  const updateMutation = useMutation<
    NotificationPreferences,
    Error,
    Partial<NotificationPreferences>,
    MutationContext
  >({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const response = await apiNotifications.put<NotificationPreferences>(
        `/v1/preferences/${userId}`,
        updates
      );
      return response.data;
    },
    onMutate: async updates => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['notification-preferences', userId] });

      // Snapshot previous value
      const previousPreferences = queryClient.getQueryData<NotificationPreferences>([
        'notification-preferences',
        userId,
      ]);

      // Optimistically update cache
      queryClient.setQueryData<NotificationPreferences>(
        ['notification-preferences', userId],
        old => ({
          ...(old ?? DEFAULT_NOTIFICATION_PREFERENCES),
          ...updates,
        })
      );

      return { previousPreferences };
    },
    onSuccess: () => {
      toast.success(t('entity.notification.preferencesUpdated'));
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousPreferences) {
        queryClient.setQueryData(['notification-preferences', userId], context.previousPreferences);
      }
      toast.error(t('error.operation', { action: t('actions.update'), entity: t('entities.preferences') }));
    },
  });

  // Check DND active status
  const checkDNDActive = useCallback(() => {
    const prefs = currentPreferences;

    // If DND is disabled, it's not active
    if (!prefs.dnd_enabled) {
      setIsDNDActive(false);
      return;
    }

    const now = new Date();
    const dayOfWeek = now.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // If it's weekend and dnd_weekends is enabled, DND is active
    if (isWeekend && prefs.dnd_weekends) {
      setIsDNDActive(true);
      return;
    }

    // Check time window
    if (prefs.dnd_start_time && prefs.dnd_end_time) {
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      const startMinutes = parseTimeToMinutes(prefs.dnd_start_time);
      const endMinutes = parseTimeToMinutes(prefs.dnd_end_time);

      setIsDNDActive(isTimeInDNDWindow(currentMinutes, startMinutes, endMinutes));
    } else {
      setIsDNDActive(false);
    }
  }, [currentPreferences]);

  // Re-check DND status every minute
  useEffect(() => {
    checkDNDActive();
    const interval = setInterval(checkDNDActive, 60 * 1000);
    return () => clearInterval(interval);
  }, [checkDNDActive]);

  // Action: Update preferences with partial data
  const updatePreferences = useCallback(
    async (updates: Partial<NotificationPreferences>): Promise<void> => {
      try {
        await updateMutation.mutateAsync(updates);
      } catch {
        // Error already handled by mutation onError
      }
    },
    [updateMutation]
  );

  // Action: Toggle a specific category
  const toggleCategory = useCallback(
    async (category: NotificationCategory, enabled: boolean): Promise<void> => {
      try {
        const updatedCategories = {
          ...currentPreferences.categories,
          [category]: {
            ...currentPreferences.categories[category],
            enabled,
          },
        };
        await updateMutation.mutateAsync({ categories: updatedCategories });
      } catch {
        // Error already handled by mutation onError
      }
    },
    [currentPreferences.categories, updateMutation]
  );

  // Action: Set category priority threshold
  const setCategoryThreshold = useCallback(
    async (category: NotificationCategory, threshold: NotificationPriority): Promise<void> => {
      try {
        const updatedCategories = {
          ...currentPreferences.categories,
          [category]: {
            ...currentPreferences.categories[category],
            priority_threshold: threshold,
          },
        };
        await updateMutation.mutateAsync({ categories: updatedCategories });
      } catch {
        // Error already handled by mutation onError
      }
    },
    [currentPreferences.categories, updateMutation]
  );

  // Action: Toggle Do Not Disturb
  const toggleDND = useCallback(
    async (enabled: boolean): Promise<void> => {
      try {
        await updateMutation.mutateAsync({ dnd_enabled: enabled });
      } catch {
        // Error already handled by mutation onError
      }
    },
    [updateMutation]
  );

  // Action: Set DND schedule
  const setDNDSchedule = useCallback(
    async (startTime: string, endTime: string, weekends: boolean): Promise<void> => {
      try {
        await updateMutation.mutateAsync({
          dnd_start_time: startTime,
          dnd_end_time: endTime,
          dnd_weekends: weekends,
        });
      } catch {
        // Error already handled by mutation onError
      }
    },
    [updateMutation]
  );

  // Action: Reset to default preferences
  const resetToDefaults = useCallback(async (): Promise<void> => {
    try {
      await updateMutation.mutateAsync(DEFAULT_NOTIFICATION_PREFERENCES);
    } catch {
      // Error already handled by mutation onError
    }
  }, [updateMutation]);

  return {
    preferences: currentPreferences,
    isLoading,
    error: queryError?.message ?? null,
    updatePreferences,
    toggleCategory,
    setCategoryThreshold,
    toggleDND,
    setDNDSchedule,
    resetToDefaults,
    isDNDActive,
  };
};

export default useNotificationPreferences;
